import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import BlockIcon from '@mui/icons-material/Block';
import { Alert, Box, Button, Drawer, IconButton, MenuItem, Select, Snackbar, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useGlobalState } from '../../../store/GlobalStore';
import { usePost } from '../../../utils/apiHelper';
import { IExternalAccountSyncBulkActionResult } from '../../../models/ExternalAccountSync/IExternalAccountSync';

const REJECT_REASONS = ['Duplicate Record', 'Invalid Data', 'Client Code Not Recognized', 'Not Authorized For Client', 'Other'];

interface RejectRecordDrawerProps {
    open: boolean;
    // Single-record mode when stagingIds has exactly one id and label is provided; bulk mode otherwise.
    stagingIds: number[];
    label?: string;
    demoMode?: boolean;
    onClose: () => void;
    onRejected: (stagingIds: number[]) => void;
}

const RejectRecordDrawer: React.FC<RejectRecordDrawerProps> = ({ open, stagingIds, label, demoMode, onClose, onRejected }) => {
    const { state } = useGlobalState();
    const actionByEmail = state.user?.email || state.userAccessContext?.userName || '';

    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setReason('');
            setComments('');
            setErrorMessage('');
        }
    }, [open]);

    const commentsRequired = reason === 'Other';
    const isValid = !!reason && (!commentsRequired || comments.trim().length > 0);
    const isBulk = stagingIds.length > 1;

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!isValid || stagingIds.length === 0) return;

        setSubmitting(true);
        setErrorMessage('');

        try {
            if (demoMode) {
                await new Promise((resolve) => setTimeout(resolve, 300));
                onRejected(stagingIds);
                setSuccessOpen(true);
                onClose();
                return;
            }

            if (isBulk) {
                const response = await usePost<IExternalAccountSyncBulkActionResult>('ExternalAccountSyncBulkReject', {
                    stagingIds,
                    rejectReason: reason,
                    rejectComments: comments,
                    actionByEmail,
                });
                onRejected(response.data?.succeededIds || []);
            } else {
                await usePost('ExternalAccountSyncReject', {
                    stagingId: stagingIds[0],
                    rejectReason: reason,
                    rejectComments: comments,
                    actionByEmail,
                });
                onRejected(stagingIds);
            }
            setSuccessOpen(true);
            onClose();
        } catch (err) {
            setErrorMessage('Failed to reject record(s). Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <Drawer open={open} anchor="right" onClose={handleClose}>
                <Box display="flex" flexDirection="column" height="100%" width={480} maxWidth="95vw" sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#007FFF', color: '#fff', padding: '14px 44px 14px 20px', flexShrink: 0 }}>
                        <BlockIcon sx={{ fontSize: 22 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Reject {isBulk ? `${stagingIds.length} Records` : (label || 'Record')}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            disabled={submitting}
                            sx={{ position: 'absolute', right: 6, top: 8, color: 'white' }}
                        >
                            <HighlightOffIcon style={{ fontSize: 26 }} />
                        </IconButton>
                    </Box>

                    <Box flexGrow={1} sx={{ padding: '20px', overflow: 'auto' }}>
                        <Box display="flex" alignItems="flex-start" sx={{ gap: 1.25, backgroundColor: '#fdecea', border: '1px solid #f6c8c2', borderRadius: 1.5, padding: '14px 16px', mb: 2.5 }}>
                            <BlockIcon fontSize="small" sx={{ color: '#b3261e', mt: '2px', flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#8c2820', lineHeight: 1.5 }}>
                                This will be recorded in the audit history{isBulk ? ` for all ${stagingIds.length} selected records` : ''}.
                            </Typography>
                        </Box>

                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#6b7368', mb: 0.5 }}>
                            Reject Reason
                        </Typography>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={reason}
                            onChange={(e: any) => setReason(e.target.value)}
                            sx={{ mb: 2.5 }}
                        >
                            <MenuItem value="" disabled>Select a reason</MenuItem>
                            {REJECT_REASONS.map((r) => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </Select>

                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#6b7368', mb: 0.5 }}>
                            Comments{commentsRequired ? ' — required' : ''}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            size="small"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder={commentsRequired ? 'Required when reason is Other' : 'Optional — recorded for the audit trail'}
                            inputProps={{ maxLength: 1000 }}
                        />

                        {errorMessage && (
                            <Typography sx={{ fontSize: '0.82rem', color: '#b3261e', mt: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" sx={{ gap: 1.25, padding: '14px 20px 20px', borderTop: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
                        <Button size="small" variant="outlined" fullWidth disabled={submitting} onClick={handleClose} sx={{ borderColor: '#c7cbd1', color: '#5b625b', textTransform: 'none', fontWeight: 600 }}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            color="error"
                            disableElevation
                            startIcon={<BlockIcon fontSize="small" />}
                            disabled={!isValid || submitting}
                            onClick={handleConfirm}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {submitting ? 'Rejecting…' : 'Confirm'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Snackbar open={successOpen} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={() => setSuccessOpen(false)}>
                <Alert onClose={() => setSuccessOpen(false)} severity="success" elevation={6} variant="filled">
                    {isBulk ? 'Records rejected.' : 'Record rejected.'}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
};

export default RejectRecordDrawer;
